import { ReactNode } from 'react';

import { Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';

import {
  createResourceLinkExtension,
  isResourceName,
} from '@spotify/confidence-core-react';

import { confidenceChatPlugin } from '../plugin';
import { chatRouteRef, projectChatRouteRef } from '../routes';

interface ChatResourceLinkProps {
  name: string;
  text: ReactNode;
  className?: string;
}

const isProjectChatName = (name: string) => {
  const parts = name.split('/');
  return parts.length === 4 && parts[0] === 'projects' && parts[2] === 'chats';
};

const StandaloneChatResourceLink = ({
  name,
  text,
  className,
}: ChatResourceLinkProps) => {
  const routeRef = useRouteRef(chatRouteRef);
  const [, chatId] = name.split('/');

  if (!routeRef) {
    return <>{text}</>;
  }

  return (
    <Link to={routeRef({ id: chatId })} className={className}>
      {text}
    </Link>
  );
};

const ProjectChatResourceLink = ({
  name,
  text,
  className,
}: ChatResourceLinkProps) => {
  const projectChatRoute = useRouteRef(projectChatRouteRef);
  const [, projectId, , chatId] = name.split('/');

  if (!projectChatRoute) {
    return <>{text}</>;
  }

  return (
    <Link
      to={projectChatRoute({ id: projectId, chatId })}
      className={className}
    >
      {text}
    </Link>
  );
};

export const ChatResourceLinkExtension = confidenceChatPlugin.provide(
  createResourceLinkExtension({
    id: 'chat-resource-link',
    predicate: ({ name }) => isResourceName(name, 'chats'),
    icon: 'chat',
    render: ({ name, text, className }) => (
      <StandaloneChatResourceLink
        name={name}
        text={text}
        className={className}
      />
    ),
  }),
);

export const ProjectChatResourceLinkExtension = confidenceChatPlugin.provide(
  createResourceLinkExtension({
    id: 'project-chat-resource-link',
    predicate: ({ name }) => isProjectChatName(name),
    icon: 'chat',
    render: ({ name, text, className }) => (
      <ProjectChatResourceLink name={name} text={text} className={className} />
    ),
  }),
);
